/**
 * The two money domains.
 *
 * `Centimes` is the certified domain: the amounts the MCF records (line
 * values, invoice TTC and PSVB, MV, the settlements declared in 35h), held
 * as integer centimes whatever the currency (DGI: two decimals).
 *
 * `Money` is the monetary domain: money that changes hands (amounts received
 * or refunded, cash tendered, change, remaining due), an integer count of
 * the minor unit of its currency. For the franc CFA the minor unit is the
 * franc itself: no centimes circulate.
 *
 * Every conversion between them, and every rounding of a certified amount to
 * the payable unit of its currency, lives here and in payable-amount.ts.
 */
/**
 * Minor-unit digits per currency, ISO 4217 (list one, the « minor unit »
 * column): XOF and XAF (franc CFA), GNF and JPY have none.
 */
export const CURRENCY_MINOR_UNITS = Object.freeze({
    XOF: 0,
    XAF: 0,
    GNF: 0,
    JPY: 0,
    EUR: 2,
    USD: 2,
    GBP: 2,
    CHF: 2,
    CAD: 2,
    NGN: 2,
    GHS: 2,
});
const FRANC_CFA = {
    symbol: "FCFA",
    singular: "franc CFA",
    plural: "francs CFA",
    feminine: false,
    hundredth: { singular: "centime", plural: "centimes" },
};
const CENTIME = { singular: "centime", plural: "centimes" };
const CENT = { singular: "cent", plural: "cents" };
/** Wording per currency, keyed like CURRENCY_MINOR_UNITS. */
export const CURRENCY_LABELS = Object.freeze({
    XOF: FRANC_CFA,
    XAF: FRANC_CFA,
    GNF: { symbol: "GNF", singular: "franc guinéen", plural: "francs guinéens", feminine: false, hundredth: null },
    JPY: { symbol: "JPY", singular: "yen", plural: "yens", feminine: false, hundredth: null },
    EUR: { symbol: "EUR", singular: "euro", plural: "euros", feminine: false, hundredth: CENTIME },
    USD: { symbol: "USD", singular: "dollar américain", plural: "dollars américains", feminine: false, hundredth: CENT },
    GBP: {
        symbol: "GBP",
        singular: "livre sterling",
        plural: "livres sterling",
        feminine: true,
        hundredth: { singular: "penny", plural: "pence" },
    },
    CHF: { symbol: "CHF", singular: "franc suisse", plural: "francs suisses", feminine: false, hundredth: CENTIME },
    CAD: { symbol: "CAD", singular: "dollar canadien", plural: "dollars canadiens", feminine: false, hundredth: CENT },
    NGN: { symbol: "NGN", singular: "naira", plural: "nairas", feminine: false, hundredth: { singular: "kobo", plural: "kobos" } },
    GHS: { symbol: "GHS", singular: "cedi", plural: "cedis", feminine: false, hundredth: { singular: "pesewa", plural: "pesewas" } },
});
/** The code of a currency reference. */
export function currencyCode(currency) {
    return typeof currency === "string" ? currency : currency.code;
}
/** Wording of a currency; refuses a currency neither its definition nor the table words. */
export function currencyLabel(currency) {
    if (typeof currency !== "string" && currency.label !== undefined)
        return currency.label;
    const code = currencyCode(currency);
    const label = CURRENCY_LABELS[code];
    if (label === undefined) {
        throw new RangeError(`Devise inconnue : « ${code} » (code ISO 4217 attendu)`);
    }
    return label;
}
/** Currencies of the franc CFA (UEMOA XOF, CEMAC XAF). */
const CFA_CURRENCIES = ["XOF", "XAF"];
/** True for a currency of the franc CFA, by its code, whatever wording a period states for it. */
export function isFrancCfa(currency) {
    return CFA_CURRENCIES.includes(currencyCode(currency));
}
/**
 * Currency of a sales document that carries none (a null currency): the
 * franc CFA of the UEMOA, which the MCF certifies.
 */
export const DEFAULT_INVOICE_CURRENCY = "XOF";
/**
 * The currency an invoice is issued and settled in: its stored `currency`.
 * The key is required, so a caller passes the document's own value; only a
 * null currency falls back to the default.
 */
export function invoiceCurrency(invoice) {
    return invoice.currency ?? DEFAULT_INVOICE_CURRENCY;
}
/** Minor-unit digits of a currency; refuses a code the table does not know. */
export function minorUnits(currency) {
    if (typeof currency !== "string")
        return currency.minorUnits;
    const digits = CURRENCY_MINOR_UNITS[currency];
    if (digits === undefined) {
        throw new RangeError(`Devise inconnue : « ${currency} » (code ISO 4217 attendu)`);
    }
    return digits;
}
/** Integer centimes of an amount, snapped so float noise decides nothing. */
export function toCentimes(amount) {
    if (!Number.isFinite(amount)) {
        throw new Error(`Montant non fini: ${amount}`);
    }
    return Math.round(Number((amount * 100).toFixed(6)));
}
/** An amount, to the centime, from integer centimes. */
export function centimesToAmount(centimes) {
    const amount = centimes / 100;
    return amount === 0 ? 0 : amount;
}
/** Centimes in one minor unit of a currency: 100 for the franc CFA, 1 for the euro. */
export function centimesPerMinorUnit(currency) {
    const digits = minorUnits(currency);
    if (digits > 2) {
        throw new RangeError(`Devise « ${currencyCode(currency)} » plus fine que le centime : non prise en charge`);
    }
    if (!Number.isInteger(digits) || digits < 0) {
        throw new RangeError(`Devise « ${currencyCode(currency)} » : nombre de décimales invalide (${digits})`);
    }
    return 10 ** (2 - digits);
}
/** The French refusal of an amount finer than the currency's minor unit. */
export function payablePrecisionMessage(currency) {
    const digits = minorUnits(currency);
    if (CFA_CURRENCIES.includes(currencyCode(currency))) {
        return "Le montant doit être un nombre entier de francs CFA";
    }
    if (digits === 0)
        return "Le montant doit être un nombre entier";
    return `Le montant doit comporter au plus ${digits} décimale${digits > 1 ? "s" : ""}`;
}
/** True when `amount` is a whole number of the currency's minor unit. */
export function hasPayablePrecision(amount, currency) {
    return Number.isFinite(amount) && toCentimes(amount) % centimesPerMinorUnit(currency) === 0;
}
/** `amount` as `Money`; throws the French refusal when it is finer than the minor unit. */
export function moneyOf(amount, currency) {
    if (!hasPayablePrecision(amount, currency)) {
        throw new RangeError(`${payablePrecisionMessage(currency)} (${amount})`);
    }
    return {
        currency: currencyCode(currency),
        minor: (toCentimes(amount) / centimesPerMinorUnit(currency)),
    };
}
/**
 * The amount a `Money` stands for, in major units. `currency` is the
 * definition of `money.currency` when the code tables do not know it.
 */
export function moneyAmount(money, currency = money.currency) {
    if (currencyCode(currency) !== money.currency) {
        throw new RangeError(`Devise « ${currencyCode(currency)} » donnée pour un montant en ${money.currency}`);
    }
    return centimesToAmount(money.minor * centimesPerMinorUnit(currency));
}
/**
 * An amount as French text for messages: thousands separated by a space,
 * the centimes after a comma only when there are any (« 1 203,60 », « 12 »).
 */
export function formatAmountFr(amount) {
    const centimes = toCentimes(amount);
    const magnitude = Math.abs(centimes);
    const units = String(Math.floor(magnitude / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    const fraction = magnitude % 100;
    const text = fraction === 0 ? units : `${units},${String(fraction).padStart(2, "0")}`;
    return centimes < 0 ? `-${text}` : text;
}
/**
 * Rounds a money amount to the centime, half away from zero: an exact
 * half-centime goes to the larger magnitude (2.205 → 2.21, −2.205 → −2.21).
 * The amount in centimes is snapped to a millionth first, so binary noise
 * (1.005 × 100 = 100.49999…) does not decide the half.
 *
 * @example
 * ```typescript
 * roundMoney(76.2711864) // => 76.27
 * roundMoney(1.8018)     // => 1.8
 * roundMoney(2.205)      // => 2.21
 * ```
 */
export function roundMoney(amount) {
    if (!Number.isFinite(amount)) {
        throw new Error(`Montant non fini: ${amount}`);
    }
    const centimes = Math.round(Number((Math.abs(amount) * 100).toFixed(6)));
    const rounded = centimes / 100;
    return amount < 0 && centimes !== 0 ? -rounded : rounded;
}
/** Sum of money amounts, rounded to the centime to drop float noise. */
export function sumMoney(amounts) {
    let total = 0;
    for (const amount of amounts)
        total += amount;
    return roundMoney(total);
}
//# sourceMappingURL=money.js.map